"use client";

import {
  TypeCheckoutConfiguration,
  TypePackageAddOnItemSkeleton,
  TypePackageDetailItem,
  TypePackageModificationItem,
} from "@contentful/types";
import { PaymentIntentResponse } from "@yardzen/rest-api-client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@yardzen/ui/components/accordion";
import { Badge } from "@yardzen/ui/components/badge";
import { Button } from "@yardzen/ui/components/button";
import { ContentfulText } from "@yardzen/ui/components/contentful-text";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@yardzen/ui/components/dialog";
import { H3 } from "@yardzen/ui/components/h3";
import { LineDivider } from "@yardzen/ui/components/line-divider";
import { Link } from "@yardzen/ui/components/link";
import { P } from "@yardzen/ui/components/p";
import { VisuallyHidden } from "@yardzen/ui/components/visually-hidden";
import { cn } from "@yardzen/ui/lib/utils";
import { Entry } from "contentful";
import { format } from "date-fns";
import Image from "next/image";
import { useRouter } from "next/navigation";

export function PackageDetailItemCheckoutSuccess({
  successId,
  email,
  packageDetailItem,
  checkoutConfiguration,
  packageModificationItem,
  packageAddonItems,
  paymentData,
}: {
  successId: string;
  email: string;
  packageDetailItem: TypePackageDetailItem<"WITHOUT_UNRESOLVABLE_LINKS">;
  checkoutConfiguration: TypeCheckoutConfiguration<"WITHOUT_UNRESOLVABLE_LINKS">;
  packageModificationItem?: TypePackageModificationItem<"WITHOUT_UNRESOLVABLE_LINKS">;
  packageAddonItems?: Entry<
    TypePackageAddOnItemSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS",
    string
  >[];
  paymentData: PaymentIntentResponse;
}) {
  const router = useRouter();
  const currentDate = new Date();
  const { checkoutDetails, checkoutImage } = packageDetailItem?.fields || {};
  const fullPackageName = packageModificationItem
    ? packageModificationItem?.fields?.name
    : packageDetailItem?.fields.fullPackageName;

  const onClose = () => {
    if (checkoutConfiguration.fields.redirectUrl) {
      router.push(checkoutConfiguration.fields.redirectUrl);
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const preventClose = (event: any) => {
    event?.preventDefault();
  };

  // TODO: Get Addons price from paymentIntent
  const getAddonPrice = (
    addon: Entry<
      TypePackageAddOnItemSkeleton,
      "WITHOUT_UNRESOLVABLE_LINKS",
      string
    >,
  ): number => {
    return addon.fields.price;
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent
        onEscapeKeyDown={preventClose}
        onPointerDownOutside={preventClose}
        className="w-full max-w-[660px] rounded-lg  border-0 bg-white p-0 max-sm:max-h-[95vh] max-sm:overflow-y-auto"
        dialogOverlayClassName="bg-[#C1DA2933]"
        dialogCloseClassName="hidden"
      >
        <VisuallyHidden>
          <DialogTitle>
            {checkoutConfiguration.fields.thankYouHeader ??
              "Thank You for Your Purchase!"}
          </DialogTitle>
        </VisuallyHidden>
        <div className="w-full max-w-[660px] text-center max-sm:mt-8 sm:absolute sm:top-[-6rem] sm:z-20 sm:-mt-1">
          <H3 className="mb-1 text-4xl tracking-normal">
            {checkoutConfiguration.fields.thankYouHeader ??
              "Thank You for Your Purchase!"}
          </H3>
          {checkoutConfiguration.fields.thankYouSubheading && (
            <P noSpacing className="font-display">
              {checkoutConfiguration.fields.thankYouSubheading}
            </P>
          )}
        </div>

        <div className="w-full overflow-auto p-4 sm:max-h-[calc(95vh-200px)] md:p-8">
          <div className="mb-2 grid w-full grid-cols-2 flex-row justify-between gap-2 md:mb-6 md:grid-cols-3">
            <Badge
              variant="secondary"
              hover={false}
              className="text-brand-primary-darker flex-col rounded-lg bg-[#ECF4BF] px-4 py-2 text-xs font-semibold"
            >
              <span className="text-xxs w-full opacity-50">Order Date</span>
              <div className="w-full">
                {format(currentDate, "MMMM dd, yyyy")}
              </div>
            </Badge>
            <Badge
              variant="secondary"
              hover={false}
              className="text-brand-primary-darker flex-col rounded-lg bg-[#ECF4BF] px-4 py-2 text-xs font-semibold"
            >
              <span className="text-xxs w-full opacity-50">Order ID</span>
              <div className="w-full break-all">{successId}</div>
            </Badge>
            <Badge
              variant="secondary"
              hover={false}
              className="text-brand-primary-darker col-span-full flex-col rounded-lg bg-[#ECF4BF] px-4 py-2 text-xs font-semibold md:col-span-1"
            >
              <span className="text-xxs w-full opacity-50">
                Confirmation email Sent to
              </span>
              <div className="w-full break-all">{email}</div>
            </Badge>
          </div>

          <div className="mb-8 grid grid-cols-1 flex-row justify-between gap-4 md:grid-cols-2">
            <Image
              src={`https:${checkoutImage?.fields.image?.fields.file?.url}`}
              alt={checkoutImage?.fields.description ?? ""}
              height={600}
              width={600}
              className="h-auto w-full rounded-lg object-cover md:h-[232px] md:max-w-[286px]"
            />
            <div>
              <LineItem
                name={fullPackageName ?? ""}
                price={paymentData.product.price / 100}
              />
              {paymentData.appliedAddons &&
                paymentData.appliedAddons.map((addon, i) => (
                  <LineItem
                    key={`pdics-${addon.id}-${i}`}
                    className="text-brand-primary-regular"
                    // TODO need to get the name from Contentful
                    name={`+ ${addon.name}`}
                    price={addon.price}
                  />
                ))}
              {checkoutDetails && (
                <div>
                  <Accordion
                    type="single"
                    collapsible
                    className="w-full"
                    defaultValue="packageDetailItem"
                  >
                    <AccordionItem
                      value="packageDetailItem"
                      className="border-none"
                    >
                      <AccordionTrigger className="pt-0 text-sm">
                        View Details
                      </AccordionTrigger>
                      <AccordionContent contentWrapperClassName="pb-0">
                        <ContentfulText
                          content={checkoutDetails}
                          className="text-brand-primary-regular font-body border-system-grayscale-20 mb-2 rounded-lg border border-dashed p-2 text-left text-[11px] font-normal leading-tight"
                          pClassName="mb-0 font-semibold"
                          ulClassName="ml-6"
                        ></ContentfulText>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              )}
            </div>
          </div>
          <LineDivider
            className="mb-4 h-2 border-black opacity-20"
            orientation="horizontal"
            variant="dotted"
          />
          <LineItem name={"Subtotal"} price={paymentData.subTotal / 100} />
          {paymentData.discount &&
            paymentData.subTotal - paymentData.total != 0 && (
              <LineItem
                name={"Discount"}
                price={-(paymentData.subTotal - paymentData.total) / 100}
              />
            )}
          <LineDivider
            className="mb-4 h-2 border-black opacity-20"
            orientation="horizontal"
            variant="dotted"
          />
          <LineItem
            name={"Total"}
            price={paymentData.total / 100}
            className="text-2xl"
            classNamePrice="text-2xl font-normal"
          />
        </div>
        <div className="w-full max-w-[660px] text-center sm:absolute sm:bottom-[-4rem] sm:z-20">
          <Button className="mx-2 w-full" asChild variant="outline">
            <Link
              className="font-body mx-0 mb-4 inline-flex whitespace-nowrap px-6 py-3 font-semibold leading-3 sm:bg-[#e9eec6]"
              href={checkoutConfiguration.fields.redirectUrl}
            >
              {checkoutConfiguration.fields.redirectUrlText}
            </Link>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  function LineItem({
    name,
    price,
    className,
    classNamePrice,
  }: {
    name: string;
    price: number;
    className?: string;
    classNamePrice?: string;
  }) {
    return (
      <div className="space-between flex w-full flex-row justify-between gap-2 pb-3">
        <div
          className={cn(
            "font-display text-brand-secondary-dark text-lg",
            className,
          )}
        >
          {name}
        </div>
        <div
          className={cn(
            "font-body text-brand-secondary-dark text-lg font-semibold",
            classNamePrice,
          )}
        >
          ${Number(price)}
        </div>
      </div>
    );
  }
}
